// frontend/src/contexts/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Cargar usuario al iniciar
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/auth/profile/');
        
        setUser(response.data);
        
        // ✅ Solo marcar como autenticado si está aprobado
        if (response.data.is_approved || response.data.role === 'super_admin') {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          // ✅ Si está en ruta protegida, redirigir a pending
          if (location.pathname.startsWith('/admin')) {
            navigate('/pending-approval', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        // ✅ Solo limpiar si el error es 401 (token inválido)
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate, location.pathname]);

  const loginWithEmail = async (email, password) => {
    try {
      const response = await api.post('/auth/login/', { email, password });
      const { status, user, tokens } = response.data;

      if (status === 'pending_approval') {
        setUser(user);
        setIsAuthenticated(false);
        // ✅ NO guardar tokens si está pendiente
        return { status: 'pending', user };
      }

      // Usuario aprobado
      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

      setUser(user);
      setIsAuthenticated(true);

      return { status: 'success', user };
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const registerWithEmail = async (formData) => {
    try {
      const response = await api.post('/auth/register/', formData);
      const { user } = response.data;
      
      setUser(user);
      setIsAuthenticated(false);

      return { status: 'pending', user };
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (googleToken) => {
    try {
      console.log('🔐 Enviando token a backend...');
      const response = await api.post('/auth/google/', { token: googleToken });
      
      console.log('📥 Respuesta del backend:', response.data);

      const { status, user, tokens } = response.data;

      if (status === 'pending_approval') {
        console.log('⏳ Usuario pendiente de aprobación');
        setUser(user);
        setIsAuthenticated(false);
        // ✅ NO guardar tokens si está pendiente
        return { status: 'pending', user };
      }

      if (status === 'success') {
        console.log('✅ Usuario aprobado, guardando tokens');
        localStorage.setItem('access_token', tokens.access);
        localStorage.setItem('refresh_token', tokens.refresh);
        api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

        setUser(user);
        setIsAuthenticated(true);

        return { status: 'success', user };
      }

    } catch (error) {
      console.error('❌ Error en loginWithGoogle:', error);
      
      // Manejar error 403 (pendiente de aprobación)
      if (error.response?.status === 403 && error.response?.data?.status === 'pending_approval') {
        const userData = error.response.data.user;
        setUser(userData);
        setIsAuthenticated(false);
        // ✅ NO guardar tokens
        return { status: 'pending', user: userData };
      }
      
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.post('/auth/logout/', { refresh: refreshToken }).catch(() => {});
      }
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      delete api.defaults.headers.common['Authorization'];
      
      setUser(null);
      setIsAuthenticated(false);
      
      navigate('/login', { replace: true });
    }
  };

  // ✅ CORREGIDO: Función hasPermission mejorada
  const hasPermission = (permission) => {
    // Si no hay usuario, no tiene permisos
    if (!user) return false;
    
    // ✅ Super admin siempre tiene todos los permisos
    if (user.role === 'super_admin') return true;
    
    // ✅ Admin siempre tiene todos los permisos
    if (user.role === 'admin') return true;
    
    // ✅ Programadores tienen todos los permisos
    if (user.is_programmer) return true;
    
    // Si no hay permiso requerido, permitir acceso
    if (!permission) return true;
    
    // Si no tiene permisos definidos, denegar
    if (!user.permissions || !Array.isArray(user.permissions)) return false;
    
    // ✅ Verificar si tiene '*' (todos los permisos) o el permiso específico
    return user.permissions.includes('*') || user.permissions.includes(permission);
  };

  // ✅ Función para refrescar el perfil del usuario
  const refreshProfile = async () => {
    try {
      const response = await api.get('/auth/profile/');
      setUser(response.data);
      
      // Actualizar estado de autenticación
      if (response.data.is_approved || response.data.role === 'super_admin') {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error refrescando perfil:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    logout,
    hasPermission,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
